<template>
  <div class="bg-white shadow-lg rounded-lg overflow-hidden">
    <div class="bg-primary text-white p-4 flex justify-between items-center">
      <h3 class="text-xl font-semibold">Group Management</h3>
      <button
        @click="goBack"
        class="bg-white text-black px-4 py-2 rounded-md hover:bg-gray-200 transition duration-300"
      >
        Back
      </button>
    </div>
    <div class="p-6 space-y-4">
      <div class="flex justify-between items-center">
        <h4 class="text-lg font-semibold text-primary">User Groups</h4>
        <div class="flex justify-between mb-4">
          <button
            @click="openAddGroupModal"
            class="bg-primary text-white px-4 py-2 rounded-md hover:bg-purple-900 transition duration-300"
          >
            Add Group
          </button>
          <button
            @click="openExcelModal"
            class="ml-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition duration-300"
          >
            Upload/Download Users
          </button>
        </div>
      </div>
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-black-500 uppercase tracking-wider"
            >
              Name
            </th>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-black-500 uppercase tracking-wider"
            >
              Department
            </th>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-black-500 uppercase tracking-wider"
            >
              Members
            </th>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-black-500 uppercase tracking-wider"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-black-200">
          <tr v-for="group in groups" :key="group.id">
            <td class="px-6 py-4 whitespace-nowrap">{{ group.name }}</td>
            <td class="px-6 py-4 whitespace-nowrap">{{ group.department }}</td>
            <td class="px-6 py-4 whitespace-nowrap">
              {{ group.members.length }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <button
                @click="editGroup(group)"
                class="text-indigo-600 hover:text-indigo-900 mr-2"
              >
                Edit
              </button>
              <button
                @click="deleteGroup(group)"
                class="text-red-600 hover:text-red-900"
              >
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Add/Edit Group Modal -->
    <div
      v-if="showModal"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center"
    >
      <div class="bg-white p-8 rounded-lg shadow-xl w-full max-w-md">
        <h2 class="text-2xl font-bold mb-4">
          {{ isEditing ? "Edit Group" : "Add Group" }}
        </h2>
        <form @submit.prevent="submitGroupForm">
          <div class="mb-4">
            <label for="name" class="block text-sm font-medium text-gray-700"
              >Name</label
            >
            <input
              type="text"
              id="name"
              v-model="currentGroup.name"
              required
              class="mt-1 px-2 block w-full rounded-md border-gray-400 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            />
          </div>
          <div class="mb-4">
            <label
              for="description"
              class="block text-sm font-medium text-gray-700"
              >Description</label
            >
            <textarea
              id="description"
              v-model="currentGroup.description"
              rows="3"
              class="mt-1 px-2 block w-full rounded-md border-gray-400 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            ></textarea>
          </div>
          <div class="mb-4">
            <label
              for="department"
              class="block text-sm font-medium text-gray-700"
              >Department</label
            >
            <select
              v-model="currentGroup.department"
              class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              <option
                v-for="dept in availableDepartments"
                :key="dept"
                :value="dept"
              >
                {{ dept }}
              </option>
            </select>
          </div>

          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700"
              >Members: {{ currentGroup.members.length }} Selected</label
            >
            <input
              type="text"
              v-model="staffSearch"
              placeholder="Search staff..."
              class="mt-1 px-2 mb-2 block w-full rounded-md border-gray-400 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            />
            <div class="flex items-center mt-2 mb-2">
              <input
                type="checkbox"
                id="showSelectedOnly"
                v-model="showSelectedOnly"
                class="rounded border-gray-400 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              />
              <label for="showSelectedOnly" class="ml-2 text-sm text-gray-700"
                >Show selected only</label
              >
            </div>
            <div class="max-h-60 overflow-y-auto">
              <div
                v-for="staff in filteredStaffList"
                :key="staff.staffId"
                class="flex items-center mt-2"
              >
                <input
                  type="checkbox"
                  :id="'staff-' + staff.staffId"
                  :value="staff.email"
                  v-model="currentGroup.members"
                  class="rounded border-gray-400 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
                <label
                  :for="'staff-' + staff.staffId"
                  class="ml-2 text-sm text-gray-700"
                >
                  {{ staff.name }} (ID: {{ staff.email }})
                </label>
              </div>
            </div>
          </div>
          <div class="flex justify-end">
            <button
              type="button"
              @click="closeModal"
              class="mr-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {{ isEditing ? "Update" : "Add" }}
            </button>
          </div>
        </form>
      </div>
    </div>
    <ExcelModal
      v-if="showExcelModal"
      :show="showExcelModal"
      :data-type="'Groups'"
      @close="closeExcelModal"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import ExcelModal from "./modals/ExcelModal.vue";

export default {
  name: "GroupManagement",
  components: {
    ExcelModal,
  },
  data() {
    return {
      showModal: false,
      isEditing: false,
      currentGroup: {
        id: "",
        name: "",
        description: "",
        members: [],
      },
      staffSearch: "",
      showSelectedOnly: false,
      showExcelModal: false,
    };
  },
  computed: {
    ...mapState("groups", ["groups"]),
    ...mapState(["staff"]),
    ...mapState({
      schools: (state) => state.schools,
      departments: (state) => state.departments,
    }),
    staffList() {
      return this.staff.map((s) => ({
        id: s.id,
        name: s.name,
        staffId: s.staffId,
        email: s.email
      }));
    },
    filteredStaffList() {
      let filtered = this.staffList.filter(
        (s) =>
          s.name.toLowerCase().includes(this.staffSearch.toLowerCase()) ||
          s.email.toLowerCase().includes(this.staffSearch.toLowerCase())
      );
      if (this.showSelectedOnly) {
        filtered = filtered.filter((s) =>
          this.currentGroup.members.includes(s.email)
        );
      }
      return filtered;
    },
    availableDepartments() {
      if (!this.schools || !this.departments) return [];
      this.schools.sort((a, b) => a.fullName.localeCompare(b.fullName));
      this.departments.sort((a, b) => a.fullName.localeCompare(b.fullName));
      return [
        ...this.schools.map((d) => d.fullName),
        ...this.departments.map((d) => d.fullName),
      ];
    },
  },
  created() {
    this.fetchGroups();
    this.$store.dispatch("fetchStaff");
    this.$store.dispatch("fetchSchools");
    this.$store.dispatch("fetchDepartments");
  },
  methods: {
    ...mapActions("groups", [
      "fetchGroups",
      "addGroup",
      "updateGroup",
      "removeGroup",
    ]),
    goBack() {
      this.$router.go(-1);
    },
    openAddGroupModal() {
      this.isEditing = false;
      this.currentGroup = { id: "", name: "", description: "", members: [] };
      this.showModal = true;
    },
    editGroup(group) {
      this.isEditing = true;
      this.currentGroup = { ...group };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    async submitGroupForm() {
      try {
        if (this.isEditing) {
          await this.updateGroup(this.currentGroup);
        } else {
          await this.addGroup(this.currentGroup);
        }
        this.closeModal();
        this.fetchGroups();
      } catch (error) {
        console.error("Error submitting group form:", error);
        alert("An error occurred while saving the group. Please try again.");
      }
    },
    async deleteGroup(group) {
      if (
        confirm(`Are you sure you want to delete the group "${group.name}"?`)
      ) {
        try {
          await this.removeGroup(group.id);
          this.fetchGroups();
        } catch (error) {
          console.error("Error deleting group:", error);
          alert(
            "An error occurred while deleting the group. Please try again."
          );
        }
      }
    },
    openExcelModal() {
      this.showExcelModal = true;
    },
    closeExcelModal() {
      this.showExcelModal = false;
    },
  },
};
</script>
  
  